import type {Props as ResultLabelProps} from '@/modules/common/components/FilterSearch/components/ResultLabel';
import type {Item} from '@/modules/common/components/FilterSearch/utils/getFiltersForSearch';

import {useCallback, useMemo} from 'react';

import {FilterCheckbox} from '@/modules/common/components/FilterCheckbox/FilterCheckbox';
import {ResultLabel} from '@/modules/common/components/FilterSearch/components/ResultLabel';
import {
  filtersWithBestPrice,
  filtersWithOptionCount,
} from '@/modules/common/models/filters';

export type Props = {
  item: Item;
  searchTerm: ResultLabelProps['searchTerm'];
  onChange: (filter: {
    id: string;
    setValue: (value: Array<string>) => void;
  }) => (params: {
    type: 'clear' | 'pop' | 'push';
    value: Array<string>;
    option?: string;
  }) => void;
};

export const ResultItem = ({item, onChange, searchTerm}: Props) => {
  const onFilterChange = useMemo(
    () => onChange(item.filter),
    [item.filter, onChange]
  );

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    ({currentTarget}) => {
      const activeFilters = item.filter.options
        .filter((option) => option.active)
        .map((option) => option.value);

      if (activeFilters.includes(currentTarget.value)) {
        const nextValue = activeFilters.filter(
          (value) => value !== currentTarget.value
        );
        if (nextValue.length === 0) {
          onFilterChange({type: 'clear', value: []});
        } else {
          onFilterChange({
            type: 'pop',
            value: nextValue,
            option: currentTarget.value,
          });
        }
      } else {
        onFilterChange({
          type: 'push',
          value: activeFilters.concat(currentTarget.value),
          option: currentTarget.value,
        });
      }
    },
    [item.filter.options, onFilterChange]
  );

  const canShowOptionCount = filtersWithOptionCount.has(item.filter.id);
  const canShowOptionBestPrice = filtersWithBestPrice.has(item.filter.id);

  return (
    <FilterCheckbox
      {...item}
      canShowOptionCount={canShowOptionCount}
      canShowOptionBestPrice={canShowOptionBestPrice}
      onChange={handleChange}
      canShowOptionCountZero={false}
      label={<ResultLabel label={item.label} searchTerm={searchTerm} />}
      testId="filter-search-result"
    />
  );
};
